import { Map, List, isImmutable } from 'immutable';

import { shapeFoldersForApp, shapeVideosForApp } from '../utils/studio/shapeForApp';
import { createController } from '../utils/controller';
import { downloadFile } from '../utils/files';
import { getFilters } from '../utils/studio/filters';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { IMAGES_PER_PAGE, MEDIA_TYPE, ORDER_BY, STUDIO_STORE_PATH } from '../constants/studio';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { EMPTY } from '../constants';

import actions from '../actions';

const storePath = STUDIO_STORE_PATH[MEDIA_TYPE.VIDEOS];

const foldersController = createController();
const videosController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    foldersController,
    videosController,
  ].forEach(abort);
}

function getShopParams(state) {
  const channel = state.getIn([...storePath, 'channel']);
  const db = state.getIn([...storePath, 'db']);
  const shopId = state.getIn([...storePath, 'shopId']);
  return { channel, db, shopId };
}

function* bootstrap(state, { channel, db, shopId }) {
  abortAll();

  yield function effect(dispatch) {
    dispatch(actions.studio.videos.getFolders());
    dispatch(actions.studio.videos.getItems());
  };

  return state.setIn(storePath, Map({ channel, db, shopId }));
}

function* cleanUp(state) {
  abortAll();

  return state.setIn(storePath, EMPTY.MAP);
}

function* createFolder(state, { callback, name }) {
  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    api.studio.videos.createFolder({ db, name, shopId })
      .then(
        ({ uuid }) => dispatch(actions.studio.videos.createFolder.fulfilled({ callback, name, uuid })),
        (error) => dispatch(actions.studio.videos.createFolder.rejected(error)),
      );
  };

  return state;
}

function* createFolderRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CREATE_FOLDER,
      })
    );
  };

  return state;
}

function* createFolderFulfilled(state, { callback, name, uuid }) {
  yield function effect(dispatch) {
    if (typeof callback === 'function') {
      callback(uuid);
    } else {
      dispatch(
        actions.notifications.add({
          type: NOTIFICATION.SUCCESS,
          message: MESSAGE.SUCCESS.CREATE_FOLDER,
        })
      );
    }

    dispatch(actions.studio.videos.getFolders());
    dispatch(actions.studio.videos.getItems({ folder: uuid }));
  };

  function updateById(byId) {
    return byId.set(uuid, Map({ count: 0, name, uuid }));
  }

  function updateIds(ids) {
    return ids.push(uuid);
  }

  return state
    .updateIn([...storePath, 'folders', 'byId'], updateById)
    .updateIn([...storePath, 'folders', 'ids'], updateIds);
}

function* deleteFolder(state, folder) {
  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    api.studio.videos.deleteFolder({ db, folder, shopId })
      .then(
        () => dispatch(actions.studio.videos.deleteFolder.fulfilled(folder)),
        (error) => dispatch(actions.studio.videos.deleteFolder.rejected(error)),
      );
  };

  return state;
}

function* deleteFolderRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DELETE_FOLDER,
      })
    );
  };

  return state;
}

function* deleteFolderFulfilled(state, uuid) {
  yield function effect(dispatch) {
    dispatch(actions.studio.videos.getFolders());
    dispatch(actions.studio.videos.getItems({ folder: null }));
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.DELETE_FOLDER,
      })
    );
  };

  function updateIds(ids) {
    return ids.filter(function filterIds(item) {
      return item !== uuid;
    });
  }

  return state
    .deleteIn([...storePath, 'folders', 'byId', uuid])
    .updateIn([...storePath, 'folders', 'ids'], updateIds);
}

function* deleteItems(state, { uuids }) {
  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    dispatch(
      actions.studio.videos.toggleItemState({
        items: uuids,
        state: 'processing',
        value: true,
      })
    );

    api.studio.videos.deleteVideos({ db, shopId, uuids })
      .then(
        () => dispatch(actions.studio.videos.deleteItems.fulfilled(shopId)),
        (error) => dispatch(actions.studio.videos.deleteItems.rejected({ error, shopId })),
      );
  };

  return state;
}

function* deleteItemsRejected(state, { error, shopId }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.DELETE_VIDEOS,
        type: NOTIFICATION.ERROR,
      })
    );

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.videos.getFolders());
      dispatch(actions.studio.videos.getItems({ clear: true }));
    }
  };

  return state;
}

function* deleteItemsFulfilled(state, shopId) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.SUCCESS.DELETE_VIDEOS,
        type: NOTIFICATION.SUCCESS,
      })
    );

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.videos.getFolders());
      dispatch(actions.studio.videos.getItems({ clear: true }));
    }
  };

  return state;
}

function* downloadItems(state, { name, uuids }) {
  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    dispatch(
      actions.studio.videos.toggleItemState({
        items: uuids,
        state: 'processing',
        value: true,
      })
    );

    api.studio.videos.downloadVideos({ db, shopId, uuids })
      .then((blob) => new Promise(function executor(resolve, reject) {
        const reader = new FileReader();

        reader.addEventListener('loadend', function onReaderLoad() {
          try {
            downloadFile({ data: reader.result, name });
            resolve();
          } catch (error) {
            reject(error);
          }
        });

        reader.readAsDataURL(blob);
      }))
      .then(
        () => dispatch(actions.studio.videos.downloadItems.fulfilled({ shopId, uuids })),
        (error) => dispatch(actions.studio.videos.downloadItems.rejected({ error, shopId, uuids })),
      );
  };

  return state;
}

function* downloadItemsRejected(state, { error, shopId, uuids }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.DOWNLOAD_VIDEOS,
        type: NOTIFICATION.ERROR,
      })
    );

    if (shopId === getShopParams(state).shopId) {
      dispatch(
        actions.studio.videos.toggleItemState({
          items: uuids,
          state: 'processing',
          value: false,
        })
      );
    }
  };

  return state;
}

function* downloadItemsFulfilled(state, { shopId, uuids }) {
  if (shopId === getShopParams(state).shopId) {
    yield function effect(dispatch) {
      dispatch(
        actions.studio.videos.toggleItemState({
          items: uuids,
          state: 'processing',
          value: false,
        })
      );
    };
  }

  return state;
}

function* getFolders(state) {
  const { db, shopId } = getShopParams(state);
  const { signal } = foldersController.start();

  yield function effect(dispatch) {
    api.studio.videos.getFolders({ db, shopId, signal })
      .then(
        (response) => dispatch(actions.studio.videos.getFolders.fulfilled(response)),
        (error) => dispatch(actions.studio.videos.getFolders.rejected({ error, signal })),
      );
  };

  return state;
}

function* getFoldersRejected(state, { error, signal }) {
  if (signal.aborted) return state;

  abort(foldersController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return state
    .setIn([...storePath, 'folders'], Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .setIn([...storePath, 'loaded'], state.hasIn([...storePath, 'items']));
}

function* getFoldersFulfilled(state, response) {
  abort(foldersController);

  let videos = state.getIn(storePath, EMPTY.MAP);
  const allVideos = Map({ count: parseInt(response.video_count) || 0, name: 'All videos' });
  const folders = response.folders.reduce(
    shapeFoldersForApp,
    videos
      .get('folders', Map({ selected: null }))
      .set('byId', EMPTY.MAP.set(null, allVideos))
      .set('ids', List([null]))
  );

  videos = videos
    .set('folders', folders)
    .set('loaded', videos.has('items'));

  if (videos.get('loaded') && !videos.getIn(['request', 'folder'])) {
    videos = videos.setIn(['request', 'folder'], folders.getIn(['byId', folders.get('selected')]));
  }

  return state.setIn(storePath, videos);
}

function* getItems(state, payload = {}) {
  const { signal } = videosController.start();
  const { db, shopId } = getShopParams(state);
  let videos = state.getIn(storePath, EMPTY.MAP).setIn(['request', 'processing'], true);
  const {
    folder = videos.getIn(['request', 'folder', 'uuid'], null),
    order = { by: videos.getIn(['order', 'by'], null), type: videos.getIn(['order', 'type'], false) },
  } = payload;

  let {
    clear = false,
    page = videos.getIn(['request', 'page'], 0),
    query = videos.getIn(['request', 'query'], ''),
  } = payload;

  if (videos.has('folders') && folder !== videos.getIn(['folders', 'selected'])) {
    videos = videos.setIn(['folders', 'selected'], folder);
  }

  if (folder !== videos.getIn(['request', 'folder', 'uuid'], null)) {
    clear = true;
    query = '';
  } else if (query !== videos.getIn(['request', 'query'], '')) {
    clear = true;
    videos = videos.set('query', query);
  } else if (order.by !== videos.getIn(['order', 'by'], null) || order.type !== videos.getIn(['order', 'type'], false)) {
    clear = true;

    if (order.by) {
      videos = videos
        .setIn(['order', 'by'], order.by)
        .setIn(['order', 'type'], order.type);
    } else {
      order.by = undefined;
      order.type = undefined;
      videos = videos
        .deleteIn(['order', 'by'])
        .deleteIn(['order', 'type']);
    }
  }

  if (!order.by) {
    order.by = ORDER_BY.CREATED_AT;
    order.type = false;
    videos = videos
      .setIn(['order', 'by'], order.by)
      .setIn(['order', 'type'], order.type);
  }

  if (clear) {
    videos = videos.delete('lastInteraction').setIn(['items', 'loading'], true);
    page = 0;
  }

  const request = Map({
    folder: videos.getIn(['folders', 'byId', folder]),
    order: Map(order),
    page,
    query,
  });

  yield function effect(dispatch) {
    api.studio.videos.getItems({ db, filters: getFilters({ order, page, query }), folder, shopId, signal })
      .then(
        (response) => dispatch(actions.studio.videos.getItems.fulfilled({ clear, request, response })),
        (error) => dispatch(actions.studio.videos.getItems.rejected({ error, request, signal })),
      );
  };

  return state.setIn(storePath, videos);
}

function* getItemsRejected(state, { error, request, signal }) {
  if (signal.aborted) return state;

  abort(videosController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  const videos = state
    .getIn(storePath, EMPTY.MAP)
    .set('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST, total: 0 }))
    .set('request', request);

  return state.setIn(storePath, videos.set('loaded', videos.has('folders')));
}

function* getItemsFulfilled(state, { clear, request, response }) {
  abort(videosController);

  let videos = state.getIn(storePath, EMPTY.MAP);
  let items = videos
    .get('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .delete('loading')
    .delete('processing')
    .set('total', parseInt(response.count));

  if (clear) {
    items = items
      .delete('selected')
      .set('byId', EMPTY.MAP)
      .set('ids', EMPTY.LIST);
  }

  videos = videos
    .set('items', response.videos.reduce(shapeVideosForApp, items))
    .set('loaded', videos.has('folders'))
    .set('request', request);

  if ((request.get('page') + 1) * IMAGES_PER_PAGE < items.get('total')) {
    videos = videos.setIn(['request', 'hasMore'], true);
  }

  if (videos.get('loaded') && !request.get('folder') && getSize(videos.getIn(['folders', 'byId']))) {
    videos = videos.setIn(['request', 'folder'],
      videos.getIn(['folders', 'byId', videos.getIn(['folders', 'selected'], null)])
    );
  }

  return state.setIn(storePath, videos);
}

function* moveItems(state, { source, target, uuids }) {
  if (source === target || !Array.isArray(uuids) || !getSize(uuids)) return state;

  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    api.studio.videos.moveVideos({ db, shopId, source, target, uuids })
      .then(
        () => dispatch(actions.studio.videos.moveItems.fulfilled({ target, uuids })),
        (error) => dispatch(actions.studio.videos.moveItems.rejected(error)),
      );
  };

  return state;
}

function* moveItemsRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.MOVE_VIDEOS,
      })
    );
  };

  return state;
}

function* moveItemsFulfilled(state, { target, uuids }) {
  const { shopId } = getShopParams(state);

  yield function effect(dispatch) {
    dispatch(actions.studio.videos.getFolders());

    if (state.getIn([...storePath, 'folders', 'selected']) || !target) {
      dispatch(actions.studio.videos.getItems({ clear: true }));
    }

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.VIDEOS_MOVED,
        folder: Map({
          title: state.getIn([...storePath, 'folders', 'byId', target, 'name']),
          uuid: target,
        }),
        shopId,
      })
    );
  };

  function reduceSelectedImages(result, uuid) {
    return result.remove(uuid);
  }

  const path = [...storePath, 'items', 'selected'];
  const selectedImages = uuids.reduce(reduceSelectedImages, state.getIn(path, EMPTY.SET));

  return state.setIn(path, selectedImages);
}

function* renameFolder(state, { folder, name }) {
  const { db, shopId } = getShopParams(state);

  yield function effect(dispatch) {
    api.studio.videos.renameFolder({ db, folder, name, shopId })
      .then(
        () => dispatch(actions.studio.videos.renameFolder.fulfilled({ name, uuid: folder })),
        (error) => dispatch(actions.studio.videos.renameFolder.rejected(error)),
      );
  };

  return state;
}

function* renameFolderRejected(state, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.RENAME_FOLDER,
      })
    );
  };

  return state;
}

function* renameFolderFulfilled(state, { name, uuid }) {
  yield function effect(dispatch) {
    dispatch(actions.studio.videos.getFolders());
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.RENAME_FOLDER,
      })
    );
  };

  function updateFolder(folder) {
    return folder.set('name', name);
  }

  return state.updateIn([...storePath, 'folders', 'byId', uuid], updateFolder);
}

function* setValue(state, { path, value }) {
  if (!path) return state;

  const fullPath = Array.isArray(path)
    ? [...storePath, ...path]
    : [...storePath, path];

  return value === undefined
    ? state.deleteIn(fullPath)
    : state.setIn(fullPath, value);
}

function* toggleItemState(state, { items, state: path, value }) {
  function reduceIds(result, uuid) {
    switch (value) {
      case true: {
        return result.add(uuid);
      }

      case false: {
        return result.remove(uuid);
      }

      default: {
        return result.has(uuid)
          ? result.remove(uuid)
          : result.add(uuid);
      }
    }
  }

  const fullPath = [...storePath, 'items', path];
  const videos = typeof items === 'string'
    ? reduceIds(state.getIn(fullPath, EMPTY.SET), items)
    : Array.isArray(items) || isImmutable(items)
      ? items.reduce(reduceIds, state.getIn(fullPath, EMPTY.SET))
      : null;

  return getSize(videos)
    ? state.setIn(fullPath, videos)
    : state.deleteIn(fullPath);
}

function* uploadFiles(state, { files }) {
  const total = getSize(files);

  if (total > 0) {
    const { channel, db, shopId } = getShopParams(state);
    const videos = state.getIn(storePath, EMPTY.MAP);
    const folder = videos.getIn(['request', 'folder', 'uuid']);

    yield function effect(dispatch) {
      dispatch(
        actions.studio.uploadFiles({
          action: actions.studio.videos.uploadFiles,
          channel,
          db,
          files,
          folder,
          shopId,
          total,
          type: MEDIA_TYPE.VIDEOS,
        })
      );
    };
  }

  return state;
}

function* uploadFilesFulfilled(state, { folder, shopId, total, uploaded }) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: getSize(uploaded) === total
          ? MESSAGE.SUCCESS.UPLOAD_FILES
          : `${getSize(uploaded)} of ${total} files uploaded`,
        type: NOTIFICATION.SUCCESS,
      })
    );

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.studio.videos.getFolders());

      if (
        !state.getIn([...storePath, 'request', 'processing']) &&
        folder === state.getIn([...storePath, 'request', 'folder', 'uuid'])
      ) {
        dispatch(actions.studio.videos.getItems({ clear: true }));
      }
    }
  };

  return state;
}

function* uploadFilesRejected(state, { error }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        message: typeof error === 'string' ? error : MESSAGE.FAIL.UPLOAD_FILES,
        type: NOTIFICATION.ERROR,
      })
    );
  };

  return state;
}

const studioVideos = {
  [actions.studio.videos.bootstrap]: bootstrap,
  [actions.studio.videos.cleanUp]: cleanUp,
  [actions.studio.videos.createFolder]: createFolder,
  [actions.studio.videos.createFolder.fulfilled]: createFolderFulfilled,
  [actions.studio.videos.createFolder.rejected]: createFolderRejected,
  [actions.studio.videos.deleteFolder]: deleteFolder,
  [actions.studio.videos.deleteFolder.fulfilled]: deleteFolderFulfilled,
  [actions.studio.videos.deleteFolder.rejected]: deleteFolderRejected,
  [actions.studio.videos.deleteItems]: deleteItems,
  [actions.studio.videos.deleteItems.fulfilled]: deleteItemsFulfilled,
  [actions.studio.videos.deleteItems.rejected]: deleteItemsRejected,
  [actions.studio.videos.downloadItems]: downloadItems,
  [actions.studio.videos.downloadItems.fulfilled]: downloadItemsFulfilled,
  [actions.studio.videos.downloadItems.rejected]: downloadItemsRejected,
  [actions.studio.videos.getFolders]: getFolders,
  [actions.studio.videos.getFolders.fulfilled]: getFoldersFulfilled,
  [actions.studio.videos.getFolders.rejected]: getFoldersRejected,
  [actions.studio.videos.getItems]: getItems,
  [actions.studio.videos.getItems.fulfilled]: getItemsFulfilled,
  [actions.studio.videos.getItems.rejected]: getItemsRejected,
  [actions.studio.videos.moveItems]: moveItems,
  [actions.studio.videos.moveItems.fulfilled]: moveItemsFulfilled,
  [actions.studio.videos.moveItems.rejected]: moveItemsRejected,
  [actions.studio.videos.renameFolder]: renameFolder,
  [actions.studio.videos.renameFolder.fulfilled]: renameFolderFulfilled,
  [actions.studio.videos.renameFolder.rejected]: renameFolderRejected,
  [actions.studio.videos.setValue]: setValue,
  [actions.studio.videos.toggleItemState]: toggleItemState,
  [actions.studio.videos.uploadFiles]: uploadFiles,
  [actions.studio.videos.uploadFiles.fulfilled]: uploadFilesFulfilled,
  [actions.studio.videos.uploadFiles.rejected]: uploadFilesRejected,
};

export default studioVideos;
